import { createSlice } from "@reduxjs/toolkit";
import {
  addProxyUrlToProfileImage,
  getIsARepost,
  removeDuplicates,
} from "../../../utils";
import { LikesProps } from "../likes/Likes";
import { LikesUtils } from "../likes/utils";
import { PostsProps } from "../posts/Posts";
import { ReactionsProps } from "../reactions/Reactions";
import { ReactionsUtils } from "../reactions/utils";
import { NewsFeedProps } from "./NewsFeed";

const initialLikesAPI: LikesProps.API = {
  dislikeAPost_API_STATUS: "idle",
  likeAPost_API_STATUS: "idle",
  getLikesAndDislikesCountForAPost_API_STATUS: "idle",
  removeLikeOrDislikeForAPost_API_STATUS: "idle",
};
const initialGetPostAPI: NewsFeedProps.GetSinglePostPostAPI = {
  getSinglePost_API_STATUS: "idle",
};

const initialState: NewsFeedProps.State = {
  page: 1,
  processed_posts: {},
  page_size: 5,
  remaining_posts: 0,
  total_count: 0,
  total_pages: 0,
  getNewsFeedPost_API_STATUS: "idle",
  ordered_post_ids: [],
  ...initialLikesAPI,
  ...initialGetPostAPI,
};

export const newsfeedSlice = createSlice({
  name: "newsfeed",
  initialState,
  reducers: {
    setGetNewsFeedPost_API_STATUS: (
      state,
      action: NewsFeedProps.setGetNewsFeedPost_API_STATUS
    ) => {
      state.getNewsFeedPost_API_STATUS = action.payload;
    },
    setPage: (state, action: NewsFeedProps.setPage) => {
      state.page = action.payload;
    },
    setNewsFeedPost: (state, action: NewsFeedProps.setNewsFeedPost) => {
      const {
        page,
        page_size,
        processed_posts,
        remaining_posts,
        total_count,
        total_pages,
        ordered_post_ids,
      } = action.payload;
      state.ordered_post_ids =
        page === 1
          ? ordered_post_ids
          : [...state.ordered_post_ids, ...ordered_post_ids];
      state.page = page;
      state.total_pages = total_pages;
      state.total_count = total_count;
      state.processed_posts =
        page === 1
          ? processed_posts
          : { ...state.processed_posts, ...processed_posts };
      state.remaining_posts = remaining_posts;
      state.page_size = page_size;

      //Handling rating interaction and reposts
      Object.values(action.payload.processed_posts).forEach((post) => {
        const rating_interaction = post?.interactions?.rating_interaction;
        if (rating_interaction)
          state.processed_posts[
            post.ID
          ].interactions.rating_interaction.users_profile_pictures =
            getProfilePicturesOfUsersWhoRatedPost(post);

        //Reposts are added to the newsfeed as a post but need not to be shown in the newsfeed, so we are not adding them to the ordered_post_ids
        const isARepost = getIsARepost(post.EmbedPostID);
        if (!isARepost) return;
        const original_post = post.original_post;
        if (!original_post) return;
        state.processed_posts[original_post.ID] = original_post;
      });
    },
    updatePollSubmitAnswerResponse: (
      state,
      action: PostsProps.updatePollSubmitAnswerResponse
    ) => {
      const id = action.payload.post_id;
      const poll_interaction =
        state.processed_posts[id]?.interactions?.poll_interaction;
      if (!poll_interaction) return;
      state.processed_posts[id].interactions.poll_interaction = action.payload;
    },
    setSubmitAnswerForARateInteractionResponse: (
      state,
      action: PostsProps.setSubmitAnswerForARateInteractionResponse
    ) => {
      const id = action.payload.post_id;
      const interactions = state.processed_posts[id]?.interactions;
      if (!interactions) return;

      const rating_interaction = action.payload;
      state.processed_posts[id].interactions.rating_interaction =
        rating_interaction;

      state.processed_posts[
        id
      ].interactions.rating_interaction.users_profile_pictures =
        getProfilePicturesOfUsersWhoRatedPost(state.processed_posts[id]);
    },
    setNewsfeedPostComment: (
      state,
      action: NewsFeedProps.setNewsfeedPostComment
    ) => {
      const { comment_id, post_id, data } = action.payload;
      const comments = state.processed_posts[post_id].post_comment;
      if (comments?.[0].id === comment_id) {
        state.processed_posts[post_id].post_comment = data;
      }
    },

    //#region Likes/Dislikes
    immediateUpdateLikesCountForAPost_LIKED: (
      state,
      action: LikesProps.immediateUpdateLikesCount
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      if (post.user_liked === "liked") {
        post.Likes = LikesUtils.removeOneLike(post.Likes);
        return;
      }
      if (post.user_liked === "disliked")
        post.Dislikes = LikesUtils.removeOneDislike(post.Dislikes);
      post.Likes = LikesUtils.addOneLike(post.Likes);
    },
    immediateUpdateLikesCountForAPost_DISLIKED: (
      state,
      action: LikesProps.immediateUpdateLikesCount
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      if (post.user_liked === "disliked") {
        post.Dislikes = LikesUtils.removeOneDislike(post.Dislikes);
        return;
      }
      if (post.user_liked === "liked")
        post.Likes = LikesUtils.removeOneLike(post.Likes);
      post.Dislikes = LikesUtils.addOneDislike(post.Dislikes);
    },
    immediateUpdateLikesCountForAPost_REMOVED: (
      state,
      action: LikesProps.immediateUpdateLikesCount
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      if (post.user_liked === "disliked")
        post.Dislikes = LikesUtils.removeOneDislike(post.Dislikes);
      else if (post.user_liked === "liked")
        post.Likes = LikesUtils.removeOneLike(post.Likes);
    },
    updateLikesCountForAPost: (
      state,
      action: LikesProps.updateLikesCountForAPost
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      post.Likes = action.payload.likes;
      post.Dislikes = action.payload.dislikes;
    },
    setLikeAPost_API_STATUS: (
      state,
      action: LikesProps.setLikeAPost_API_STATUS
    ) => {
      state.likeAPost_API_STATUS = action.payload;
    },
    setDislikeAPost_API_STATUS: (
      state,
      action: LikesProps.setDislikeAPost_API_STATUS
    ) => {
      state.dislikeAPost_API_STATUS = action.payload;
    },
    setGetLikesAndDislikesCountForAPost_API_STATUS: (
      state,
      action: LikesProps.setGetLikesAndDislikesCountForAPost_API_STATUS
    ) => {
      state.getLikesAndDislikesCountForAPost_API_STATUS = action.payload;
    },
    setRemoveLikeOrDislikeForAPost_API_STATUS: (
      state,
      action: LikesProps.setRemoveLikeOrDislikeForAPost_API_STATUS
    ) => {
      state.removeLikeOrDislikeForAPost_API_STATUS = action.payload;
    },
    setIsUserLiked: (state, action: LikesProps.setIsUserLiked) => {
      const { post_id, voteType } = action.payload;
      if (!post_id) return;

      state.processed_posts[post_id].user_liked = voteType;
    },
    //#endregion

    //#region Reactions
    immediateUpdateForReactions_ADD: (
      state,
      action: ReactionsProps.immediateUpdateForReactions_ADD
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      post.user_reacted = action.payload.user_reacted;
      post.total_curated_reactions = ReactionsUtils.addOneReaction(
        post.total_curated_reactions
      );

      const reactionCount =
        post.reaction_counts[action.payload.user_reacted.reaction_name];
      if (reactionCount) reactionCount.count += 1;
      else
        post.reaction_counts[action.payload.user_reacted.reaction_name] = {
          count: 1,
          primary_emoji: action.payload.user_reacted.reaction_primary_reaction,
        } as ReactionsProps.ReactionProperty;
    },
    immediateUpdateForReactions_REMOVE: (
      state,
      action: ReactionsProps.immediateUpdateForReactions_REMOVE
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      post.user_reacted = action.payload.user_reacted;

      post.total_curated_reactions = ReactionsUtils.removeOneReaction(
        post.total_curated_reactions
      );

      const previousReactionCount =
        post.reaction_counts[action.payload.previous_reaction_name];
      if (previousReactionCount) previousReactionCount.count -= 1;
      if (previousReactionCount.count === 0) {
        delete post.reaction_counts[action.payload.previous_reaction_name];
      }
    },
    immediateUpdateForReactions_UPDATE: (
      state,
      action: ReactionsProps.immediateUpdateForReactions_UPDATE
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      post.user_reacted = action.payload.user_reacted;

      const currentReactionCount =
        post.reaction_counts[action.payload.user_reacted.reaction_name];
      if (currentReactionCount) currentReactionCount.count += 1;
      else
        post.reaction_counts[action.payload.user_reacted.reaction_name] = {
          count: 1,
          primary_emoji: action.payload.user_reacted.reaction_primary_reaction,
        } as ReactionsProps.ReactionProperty;

      const previousReactionCount =
        post.reaction_counts[action.payload.previous_reaction_name];
      if (previousReactionCount) previousReactionCount.count -= 1;
      if (previousReactionCount.count === 0)
        delete post.reaction_counts[action.payload.previous_reaction_name];
    },
    updateReactionCountForAPost: (
      state,
      action: ReactionsProps.updateReactionCountForAPost
    ) => {
      const post = state.processed_posts[action.payload.post_id];
      if (!post) return;
      post.total_curated_reactions = action.payload.total_curated_reactions;
      post.user_reacted = action.payload.user_reacted;
    },
    reset: () => {
      return initialState;
    },
    //#endregion

    //#region Single post
    setSinglePostResponse: (
      state,
      action: NewsFeedProps.setSinglePostResponse
    ) => {
      //Handling posts
      const post = action.payload as PostsProps.PostData;
      if (!post) return;
      state.processed_posts[post.ID] = post;
      state.ordered_post_ids = removeDuplicates([
        post.ID,
        ...state.ordered_post_ids,
      ]);

      //Handling rating interaction
      const rating_interaction = post?.interactions?.rating_interaction;
      if (rating_interaction)
        state.processed_posts[
          post.ID
        ].interactions.rating_interaction.users_profile_pictures =
          getProfilePicturesOfUsersWhoRatedPost(post);

      //Reposts are added to the newsfeed as a post but need not to be shown in the newsfeed, so we are not adding them to the ordered_post_ids
      const isARepost = !!action.payload.EmbedPostID;
      if (!isARepost) return;
      const original_post = action.payload.original_post;
      if (!original_post) return;
      state.processed_posts[original_post.ID] = original_post;
    },
    setGetSinglePostAPIStatus: (
      state,
      action: NewsFeedProps.setGetSinglePostAPIStatus
    ) => {
      state.getSinglePost_API_STATUS = action.payload;
    },
    //#endregion
  },
});

export const { actions: newsfeedActions, reducer: newsfeedReducer } =
  newsfeedSlice;

const getProfilePicturesOfUsersWhoRatedPost = (post: PostsProps.PostData) => {
  const rating_interaction = post?.interactions?.rating_interaction;
  if (!rating_interaction) return [];

  const users_voted_list = rating_interaction.users_voted_list;
  if (!users_voted_list) return [];
  const users_list = Object.values(users_voted_list);
  if (!users_list || !users_list.length) return [];
  const users: PostsProps.RatedUser[] = [];
  users_list.forEach((user) => users.push(...user.users));

  return users.map((user) => addProxyUrlToProfileImage(user.profile_picture));
};
